import {
  CarrierType,
  CarrierVisitDirection,
  OrderResponseDto,
  OrderStatus,
} from '@planning/app/api'
import { simplifyWeightMeasurementUnit } from '@planning/app/utils'
import { useTranslate } from '@tolgee/react'
import {
  ColoredLabel,
  MultiLineTextCell,
  OptionsButton,
  TomTable,
  useDefaultMRTOptions,
  useTomTable,
} from '@tom-ui/ui'
import { useTablePreferencesConfig } from '@tom-ui/utils'
import { MRT_ColumnDef } from 'material-react-table'
import { useMemo } from 'react'
import { columnFilterPresets } from '../../VesselVisit/tableConfigs/columnFilterConfigs'
import { GeneralCargoOrderDetailPanel } from './GeneralCargoOrderDetailPanel'
import { GeneralCargoOrderLocationItem } from './GeneralCargoOrderLocationItem'

interface GeneralCargoOrdersTableProps {
  orders: OrderResponseDto[]
  orderLocationsById: Record<number, GeneralCargoOrderLocationItem[]>
  orderReservationsById: Record<number, GeneralCargoOrderLocationItem[]>
  damageAmountsByOrderId: Record<number, number>
  customerNamesById: Record<number, string>
  customerNamesByReferenceId: Record<number, string>
  packageNamesById: Record<number, string>
  onEdit: (order?: OrderResponseDto) => void
  onOpenReservation?: (order: OrderResponseDto) => void
  onDelete?: (order: OrderResponseDto) => void | Promise<void>
}

export const formatOrderLocations = (orderLocations?: GeneralCargoOrderLocationItem[]) => {
  if (!orderLocations?.length) {
    return ''
  }

  return orderLocations
    .map(location => {
      const locationName = [location.generalCargoArea, location.generalCargoLocation]
        .filter(Boolean)
        .join('.')
      return locationName ? `${locationName} ${location.quantity}` : `${location.quantity}`
    })
    .join(', ')
}

const getCarrierVisitTypeLabel = (
  t: (key: string, defaultValue: string) => string,
  type?: CarrierType,
) => {
  switch (type) {
    case CarrierType.Truck:
      return t('truck', 'Truck')
    case CarrierType.Train:
      return t('train', 'Train')
    case CarrierType.Vessel:
      return t('vessel', 'Vessel')
    default:
      return '-'
  }
}

export const GeneralCargoOrdersTable = ({
  orders,
  orderLocationsById,
  orderReservationsById,
  damageAmountsByOrderId,
  customerNamesById,
  customerNamesByReferenceId,
  packageNamesById,
  onEdit,
  onOpenReservation,
  onDelete,
}: GeneralCargoOrdersTableProps) => {
  const { t } = useTranslate()
  const tableData = useMemo(
    () => orders.slice(),
    [orders, orderLocationsById, orderReservationsById],
  )

  const columns = useMemo<MRT_ColumnDef<OrderResponseDto>[]>(
    () => [
      {
        accessorKey: 'commodityName',
        header: t('commodity', 'Commodity'),
        size: 180,
        ...columnFilterPresets.searchMandatory(),
      },
      {
        accessorKey: 'referenceNumber',
        header: t('referenceNo', 'Reference no.'),
        size: 160,
        ...columnFilterPresets.searchMandatory(),
      },
      {
        accessorKey: 'lotNumber',
        header: t('lotNo', 'LOT no.'),
        size: 140,
        ...columnFilterPresets.searchOptional(),
      },
      {
        id: 'status',
        header: t('status', 'Status'),
        size: 140,
        accessorFn: row =>
          row.status === OrderStatus.Open ? t('open', 'Open') : t('fulfilled', 'Fulfilled'),
        Cell: cell => (
          <ColoredLabel
            sx={{ display: 'inline-block' }}
            label={
              cell.row.original.status === OrderStatus.Open
                ? t('open', 'Open')
                : t('fulfilled', 'Fulfilled')
            }
            color={cell.row.original.status === OrderStatus.Open ? 'info' : 'success'}
          />
        ),
        ...columnFilterPresets.openFulfilledSelect(),
      },
      {
        id: 'customerName',
        header: t('customer', 'Customer'),
        size: 160,
        accessorFn: row =>
          row.customerName?.trim() ||
          (row.customerId ? customerNamesById[row.customerId] : '') ||
          (row.customerReferenceId ? customerNamesByReferenceId[row.customerReferenceId] : '') ||
          '',
        Cell: ({ cell }) => (cell.getValue() as string) || '-',
        ...columnFilterPresets.searchOptional(),
      },
      {
        id: 'direction',
        header: t('direction', 'Direction'),
        size: 140,
        accessorFn: row =>
          row.direction === CarrierVisitDirection.Inbound
            ? t('inbound', 'Inbound')
            : t('outbound', 'Outbound'),
        filterVariant: 'select',
        filterSelectOptions: [t('inbound', 'Inbound'), t('outbound', 'Outbound')],
        filterFn: 'equals',
        enableColumnFilterModes: false,
      },
      {
        id: 'plannedLocation',
        header: t('plannedLocation', 'Planned location'),
        size: 180,
        accessorFn: row => {
          return formatOrderLocations(orderReservationsById[row.id]) ?? ''
        },
        Cell: ({ cell }) => (cell.getValue() as string) || '-',
        ...columnFilterPresets.searchOptional(),
      },
      {
        id: 'currentLocation',
        header: t('currentLocation', 'Current location'),
        size: 200,
        accessorFn: row => {
          if (row.direction === CarrierVisitDirection.Outbound) {
            return 'N/A'
          }
          return formatOrderLocations(orderLocationsById[row.id]) ?? ''
        },
        Cell: ({ cell }) => (cell.getValue() as string) || '-',
        ...columnFilterPresets.searchOptional(),
      },
      {
        accessorKey: 'plannedCargoAmount',
        header: t('plannedQuantity', 'Planned quantity'),
        minSize: 170,
        ...columnFilterPresets.numericMandatory(),
      },
      {
        accessorKey: 'actualCargoAmount',
        header: t('actualQuantity', 'Actual quantity'),
        minSize: 160,
        ...columnFilterPresets.numericOptional(),
      },
      {
        id: 'damagedAmount',
        header: t('damaged', 'Damaged'),
        size: 140,
        accessorFn: row => {
          const amount = damageAmountsByOrderId[row.id]
          return amount && amount > 0 ? amount : undefined
        },
        Cell: ({ cell }) => (cell.getValue() as string | number) || '-',
        ...columnFilterPresets.numericOptional(),
      },
      {
        accessorKey: 'grossWeight',
        header: t('weight', 'Weight'),
        size: 120,
        ...columnFilterPresets.numericOptional(),
      },
      {
        id: 'unit',
        header: t('unit', 'Unit'),
        size: 110,
        accessorFn: row => simplifyWeightMeasurementUnit(row.weightUnit),
        Cell: ({ cell }) => (cell.getValue() as string) || '-',
        ...columnFilterPresets.searchOptional(),
      },
      {
        id: 'transport',
        header: t('transport', 'Transport'),
        size: 140,
        accessorFn: row => getCarrierVisitTypeLabel(t, row.carrierVisitType),
        ...columnFilterPresets.searchOptional(),
      },
      {
        id: 'packageName',
        header: t('packaging', 'Packaging'),
        size: 140,
        accessorFn: row =>
          row.packageName?.trim() || (row.packageId ? packageNamesById[row.packageId] : '') || '',
        Cell: ({ cell }) => (cell.getValue() as string) || '-',
        ...columnFilterPresets.searchOptional(),
      },
      {
        id: 'unitId',
        header: t('id', 'ID'),
        size: 140,
        maxSize: 140,
        accessorFn: row => row.identifiableItems?.join(', ') ?? '',
        Cell: cell => <MultiLineTextCell value={cell.renderedCellValue || '-'} />,
        muiTableBodyCellProps: {
          sx: {
            whiteSpace: 'normal',
            '& .Mui-TableBodyCell-Content': {
              whiteSpace: 'normal',
              overflowWrap: 'anywhere',
              wordBreak: 'break-word',
            },
          },
        },
        ...columnFilterPresets.searchOptional(),
      },
      {
        id: 'imo',
        header: t('imo', 'IMO'),
        size: 120,
        accessorFn: row =>
          Array.isArray(row.imoClasses) && row.imoClasses.length > 0
            ? row.imoClasses.join(', ')
            : 'No',
        ...columnFilterPresets.arrayContainsValue(),
      },
    ],
    [
      t,
      orderReservationsById,
      orderLocationsById,
      damageAmountsByOrderId,
      customerNamesById,
      customerNamesByReferenceId,
      packageNamesById,
    ],
  )

  const defaultOptions = useDefaultMRTOptions<OrderResponseDto>()

  const { savedPreferences, onPreferencesChange } = useTablePreferencesConfig(
    'planning/general-cargo-orders',
  )

  const table = useTomTable<OrderResponseDto>(
    {
      columns,
      data: tableData,
      getRowId: row => row.id.toString(),

      enableTopToolbar: false,
      enableRowSelection: false,
      enableSelectAll: false,
      enableMultiRowSelection: false,
      enableBatchRowSelection: false,
      enableExpanding: true,
      enableExpandAll: true,
      getRowCanExpand: row => !!row.original.handlingInstructions?.trim(),
      renderDetailPanel: ({ row }) =>
        row.original.handlingInstructions?.trim() ? (
          <GeneralCargoOrderDetailPanel row={row} />
        ) : null,
      enableRowActions: true,
      positionActionsColumn: 'first',
      displayColumnDefOptions: {
        'mrt-row-expand': {
          header: '',
          muiTableBodyCellProps: { align: 'right' },
        },
        'mrt-row-actions': {
          header: '',
        },
      },
      muiTableContainerProps: {
        sx: {
          maxHeight: 'calc(100vh - 220px)',
          minHeight: 'calc(100vh - 220px)',
        },
      },
      renderRowActions: ({ row }) => (
        <OptionsButton
          options={[
            {
              label: t('edit', 'Edit'),
              onClick: () => onEdit(row.original),
            },
            ...(onDelete && row.original.status === OrderStatus.Open
              ? [
                  {
                    label: t('delete', 'Delete'),
                    onClick: () => onDelete(row.original),
                  },
                ]
              : []),
            ...(onOpenReservation && row.original.status === OrderStatus.Open
              ? [
                  {
                    label: t('reservation', 'Reservation'),
                    onClick: () => onOpenReservation(row.original),
                  },
                ]
              : []),
          ]}
          position='right'
        />
      ),
      initialState: {
        ...defaultOptions.initialState,
        columnPinning: {
          left: [
            'mrt-row-actions',
            'mrt-row-expand',
            'commodityName',
            'referenceNumber',
            'lotNumber',
          ],
        },
      },
    },
    undefined,
    undefined,
    { columns, savedPreferences, onPreferencesChange },
  )

  return <TomTable table={table} />
}
